import React, { Fragment, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Tooltip } from "../Tooltip";
import {
  UserImpactAggregates,
  ImpactType,
  putCommasIntoNumber,
  User,
  Wordpress,
} from "@ecologi/shared";
import {
  HowCalculatedModalImpact,
  HowCalculatedModalTrees,
} from "../HowCalculatedModal";
import { Typography } from "../Typography";
import { CardType, IconMapper, IconWrapper } from "./ProfileCardIcons";
import styles from "./ProfileCard.module.scss";
import HelpIcon from "@ecologi/assets/icons/help.svg";

const CardTitle = styled(Typography)<{ isMonths: boolean }>`
  font-weight: bold !important;

  ${({ isMonths }) =>
    !isMonths &&
    css`
      display: flex;
      flex-direction: row;
      justify-content: center;
    `}

  @media ${({ theme }) => theme.bp.mobileUp} {
    ${({ isMonths }) =>
      isMonths &&
      css`
        display: flex;
        flex-direction: row;
        justify-content: center;
      `}
  }
`;

const CardTitleMonthSuffix = styled(Typography)`
  font-weight: bold !important;

  @media ${(props) => props.theme.bp.mobileUp} {
    margin-left: 5px;
    display: flex;
  }
`;

const InfoButton = styled(HelpIcon)`
  background-color: ${(props) => props.theme.colors.charcoal};
  border-radius: 99px;
  border: none;
  cursor: pointer;
  display: block;
  margin-left: 10px;
  height: 21px;
  width: 21px;
  padding: 5px 7px;

  position: absolute;
  top: 50px;
  left: 53%;

  &:hover {
    opacity: 0.7;
  }

  path {
    fill: ${(props) => props.theme.colors.snowdrop};
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    top: 82px;
    left: 54%;
  }
`;

export type ProfileCardProps = {
  userImpactAggregates: UserImpactAggregates;
  formattingFn?: (n: number) => string;
  isOwnProfile?: boolean;
  number: number;
  projects: Wordpress.Project[];
  type: CardType;
  user: User;
};

type ProfileCardLoadingProps = {
  text?: React.ReactNode;
  type: CardType;
};

export const ProfileCardLoading = ({ type, text }: ProfileCardLoadingProps) => {
  return (
    <Fragment>
      <div className={`${styles.card} ${styles[type]}`}>
        <div className={styles.cardNumber}>
          <div className={styles.cardNumberWrapper}></div>
        </div>
        <div className={styles.cardText}>{text}</div>
      </div>
    </Fragment>
  );
};

export const DeprecatedProfileCard = ({
  userImpactAggregates,
  formattingFn = putCommasIntoNumber,
  isOwnProfile,
  number,
  projects,
  type,
  user,
}: ProfileCardProps) => {
  const [isModalOpen, setModalState] = useState(false);
  const isCarbon = type === "carbon";
  const isTree = type === "trees";
  const isMonths = type === "months";
  const isMembers = type === "members";

  let text = "of climate impact";
  let tooltipText = "How is this calculated?";
  let labelText = `More details about ${
    isCarbon ? "carbon" : "trees"
  } calculation`;
  let suffixText: string | React.ReactElement = "";
  let infoModal: null | React.ReactElement = null;

  if (isMonths) {
    suffixText = (
      <CardTitleMonthSuffix use="subtitle2" as="p">
        month{number === 1 ? "" : "s"}
      </CardTitleMonthSuffix>
    );
  } else if (isCarbon) {
    text = "of carbon avoidance";
    suffixText = "t";
    infoModal = (
      <HowCalculatedModalImpact
        isOpen={isModalOpen}
        impactType={ImpactType.CARBON_OFFSETS}
        aggregateOfUserImpact={userImpactAggregates.CARBON_OFFSETS}
        onClose={() => setModalState(false)}
        user={user}
        // Legacy profile cards do not support pending totals.
        pendingTotal={0}
      />
    );
  } else if (isTree) {
    text = isOwnProfile ? "trees in your forest" : "trees in their forest";
    infoModal = (
      <HowCalculatedModalTrees
        isOpen={isModalOpen}
        onClose={() => setModalState(false)}
        projectsIncludingTrees={projects}
        user={user}
        // Legacy profile cards do not support pending totals.
        pendingTotal={0}
      />
    );
  } else if (isMembers) {
    text = "members";
  }

  return (
    <Fragment>
      <div
        className={`${styles.card} ${styles[type]}`}
        data-bespoke={user.username}
      >
        <div className={styles.cardNumber}>
          <IconWrapper type={type}>{IconMapper[type]}</IconWrapper>
          <CardTitle use="subtitle2" as="p" isMonths={isMonths}>
            {formattingFn(number)}
            {suffixText}
            {infoModal && (
              <Tooltip content={tooltipText}>
                <InfoButton
                  aria-label={labelText}
                  onClick={(e) => {
                    e.preventDefault();
                    setModalState(true);
                  }}
                />
              </Tooltip>
            )}
          </CardTitle>
        </div>
        <div className={styles.cardText}>{text}</div>
      </div>
      {infoModal}
    </Fragment>
  );
};
